import type { ListParams } from './types';

// 与 core-api 共享同一会话存储键，便于管理后台与控制台两个入口共享登录态。
const TOKEN_KEY = 'integral_token';

export class ApiError extends Error {
  status: number;
  /** 后端 4xx 附带的结构化详情（如删除拦截的 details.blockers 明细单），供前台逐条展示。 */
  details?: unknown;
  /**
   * 后端附带的可编程错误码（如 FINANCE_READ_DISABLED / ATTACHMENT_FILE_MISSING）。
   * 有了它，前端才能区分「本环境未启用该能力」与「真的出错了」——
   * 只看 HTTP 状态码时这两者都是 5xx/4xx，会被统一显示成故障。
   */
  code?: string;
  constructor(message: string, status: number, details?: unknown, code?: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.details = details;
    this.code = code;
  }
}

export function getToken(): string {
  try { return localStorage.getItem(TOKEN_KEY) || ''; } catch { return ''; }
}
export function setToken(token: string): void {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch { /* storage unavailable */ }
}

// 会话失效时的回调（由 AuthProvider 注册，用于跳登录页）。
let unauthorizedHandler: (() => void) | null = null;
export function setUnauthorizedHandler(fn: (() => void) | null): void {
  unauthorizedHandler = fn;
}

export interface DataEnvelope<T> { data: T; }

function buildQuery(params?: ListParams): string {
  if (!params) return '';
  const usp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '') continue;
    usp.set(k, String(v));
  }
  const s = usp.toString();
  return s ? `?${s}` : '';
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = getToken();
  const headers: Record<string, string> = {
    'content-type': 'application/json',
    ...(token ? { authorization: `Bearer ${token}` } : {}),
    ...((init.headers as Record<string, string>) || {}),
  };
  let res: Response;
  try {
    res = await fetch(path, { ...init, headers });
  } catch {
    // P0-1：不再硬编码端口。实际目标由 vite 代理的 VITE_API_TARGET 决定，
    // 这里给出可自证的排查路径：先 /health 确认代理通不通，再看返回的 env_name / core.base_url。
    throw new ApiError(
      '无法连接核心服务。请确认 core-api 已启动，并访问 /health 核对环境名与实际目标地址（勿凭端口猜测）。',
      0,
    );
  }
  if (res.status === 401) {
    if (unauthorizedHandler) unauthorizedHandler();
  }
  const ct = res.headers.get('content-type') || '';
  const body: unknown = ct.includes('json') ? await res.json().catch(() => null) : await res.text().catch(() => '');
  if (!res.ok) {
    const obj = body && typeof body === 'object' ? (body as Record<string, unknown>) : null;
    const msg = obj && 'error' in obj ? String(obj.error) : `请求失败 ${res.status}`;
    throw new ApiError(msg, res.status, obj?.details, obj?.code ? String(obj.code) : undefined);
  }
  return body as T;
}

// 通用：解 {data} 信封。
function unwrap<T>(body: DataEnvelope<T> | T): T {
  if (body && typeof body === 'object' && 'data' in (body as Record<string, unknown>)) {
    return (body as DataEnvelope<T>).data;
  }
  return body as T;
}

/**
 * 下载二进制文件（如 ARCA 三联发票 PDF）。
 *
 * 为什么不用 `<a href>` 直连：这些下载端点要求 `Authorization` 头，而浏览器对普通链接
 * 不会带自定义头 —— 结果是 401，用户看到的是「文件不见了 / 打不开」而不是「没登录」。
 * 所以先带令牌 fetch 成 blob，再用临时 `<a download>` 触发保存。
 *
 * 失败不静默：非 2xx 时按 JSON 解析出 `error` / `code`，抛与 request 同语义的 ApiError，
 * 调用方（如财务页）才能按 code 区分「本票 PDF 未归档」这类流程状态与真的故障。
 */
export async function downloadFile(path: string, fallbackName: string): Promise<void> {
  const token = getToken();
  let res: Response;
  try {
    res = await fetch(path, { headers: token ? { authorization: `Bearer ${token}` } : {} });
  } catch {
    throw new ApiError(
      '无法连接核心服务。请确认 core-api 已启动，并访问 /health 核对环境名与实际目标地址（勿凭端口猜测）。',
      0,
    );
  }
  if (res.status === 401) {
    if (unauthorizedHandler) unauthorizedHandler();
  }
  if (!res.ok) {
    const ct = res.headers.get('content-type') || '';
    let body: Record<string, unknown> | null = null;
    if (ct.includes('json')) {
      body = (await res.json().catch(() => null)) as Record<string, unknown> | null;
    }
    const msg = body && 'error' in body ? String(body.error) : `下载失败 ${res.status}`;
    throw new ApiError(msg, res.status, body?.details, body?.code ? String(body.code) : undefined);
  }
  const blob = await res.blob();
  // 文件名用调用方给的 fallback：Content-Disposition 里的 filename 可能是 latin-1 编码，
  // 直接拿来当 download 名在中文/特殊字符上会乱码；调用方拼的名字（ARCA_<内部编号>.pdf）稳定可读。
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fallbackName;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  a.remove();
  // 立即 revoke 会让部分浏览器在下载真正开始前就丢掉 blob，表现为「点了没反应」。
  // 延后释放，让下载先把字节取走（与 AttachmentPanel 的下载同一处理）。
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

export const api = {
  get: <T>(path: string) => request<T>(path, { method: 'GET' }),
  // 列表与详情统一走 {data} 信封
  list: async <T>(path: string, params?: ListParams): Promise<T[]> =>
    unwrap<T[]>(await request<DataEnvelope<T[]>>(`${path}${buildQuery(params)}`)),
  detail: async <T>(path: string): Promise<T> =>
    unwrap<T>(await request<DataEnvelope<T>>(path)),
  post: <T>(path: string, data?: unknown) =>
    request<T>(path, { method: 'POST', body: JSON.stringify(data ?? {}) }),
  put: <T>(path: string, data?: unknown) =>
    request<T>(path, { method: 'PUT', body: JSON.stringify(data ?? {}) }),
  patch: <T>(path: string, data?: unknown) =>
    request<T>(path, { method: 'PATCH', body: JSON.stringify(data ?? {}) }),
  del: <T>(path: string) =>
    request<T>(path, { method: 'DELETE' }),
};
